import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { getJson, mutateJson } from '../api/client';
import { Field, Input, Select } from '../components/form';
import { Alert, Badge, Button, Card, EmptyState, Spinner } from '../components/ui';
import { brl, dataUtcBr, mesAno } from '../lib/format';
import { navigate } from '../router';
import { type Rascunho, Secoes } from './RascunhoBalancete';
import styles from './screens.module.css';

interface Competencia {
  id: number;
  ano: number;
  mes: number;
}
type StatusBalancete = 'rascunho' | 'revisao' | 'publicado';
interface Versao {
  id: number;
  versao: number;
  status: StatusBalancete;
  resultado: number | null;
  pdfDocumentoId: number | null;
  publicadoEm: string | null;
}
interface Divergencia {
  saldoCalculado: number;
  saldoDeclarado: number;
  diferenca: number;
}
interface Versoes {
  balancetes: Versao[];
  fechamento: { divergente: boolean; divergencias: Divergencia[] };
}

type CargaRascunho =
  | { estado: 'carregando' }
  | { estado: 'erro' }
  | { estado: 'ok'; rascunho: Rascunho };
type CargaVersoes =
  | { estado: 'carregando' }
  | { estado: 'erro' }
  | { estado: 'ok'; dados: Versoes };
type Aviso = { kind: 'success' | 'danger'; texto: string };

const STATUS: Record<StatusBalancete, string> = {
  rascunho: 'Rascunho',
  revisao: 'Em revisão',
  publicado: 'Publicado',
};
const MSG_SEM_ARQUIVO = 'Escolha o arquivo PDF do balancete.';
const MSG_NAO_PDF = 'O arquivo precisa ser um PDF.';
const MSG_DIVERGENCIA =
  'O fechamento do mês aponta divergência de saldo. Corrija a conferência do extrato antes de publicar.';
/** Erros da API (envio e publicação) → mensagem. */
const ERRO_GERAL: Record<string, string> = {
  tenant_suspended: 'Condomínio suspenso — somente leitura.',
  file_too_large: 'O arquivo passa do limite de 25 MB.',
  balancete_publicado: 'Este balancete já foi publicado: a edição está travada.',
  already_published: 'Este balancete já foi publicado.',
  competencia_not_found: 'Competência não encontrada.',
};
const ERRO_ENVIO_PADRAO = 'Não foi possível enviar o balancete. Tente novamente.';
const ERRO_PUBLICAR_PADRAO = 'Não foi possível publicar o balancete. Tente novamente.';

/**
 * Balancete do mês (RF-A14, RF-A19): o rascunho das 11 seções (regime de caixa), a versão
 * enviada, o envio do PDF e a publicação. Tudo que é número vem do servidor (rascunho,
 * resultado e divergência do fechamento); a tela só formata. `editavel` só para o síndico
 * de condomínio ativo (enviar e publicar são `requireRole('sindico')`; contábil e tenant
 * suspenso, #127, só leem). Publicado trava envio e publicação; o servidor também recusa.
 */
export function BalanceteDoMes({ editavel }: { editavel: boolean }) {
  const [comps, setComps] = useState<Competencia[] | 'erro' | null>(null);
  const [sel, setSel] = useState('');
  const [rascunho, setRascunho] = useState<CargaRascunho>({ estado: 'carregando' });
  const [versoes, setVersoes] = useState<CargaVersoes>({ estado: 'carregando' });
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erroArquivo, setErroArquivo] = useState<string | null>(null);
  // Muda a cada envio concluído ou troca de competência para esvaziar o campo de arquivo.
  const [rodada, setRodada] = useState(0);
  // Competência selecionada no momento: respostas de outra (troca rápida) são descartadas.
  const selAtual = useRef(sel);
  selAtual.current = sel;

  useEffect(() => {
    void getJson<{ competencias: Competencia[] }>('/api/competencias').then((d) => {
      if (!d) {
        setComps('erro');
        return;
      }
      setComps(d.competencias);
      if (d.competencias[0]) setSel(String(d.competencias[0].id));
    });
  }, []);

  const carregarVersoes = useCallback(async (competencia: string) => {
    const dados = await getJson<Versoes>(
      `/api/competencias/${encodeURIComponent(competencia)}/balancetes`,
    );
    if (selAtual.current !== competencia) return;
    setVersoes(dados ? { estado: 'ok', dados } : { estado: 'erro' });
  }, []);

  useEffect(() => {
    if (!sel) return;
    setRascunho({ estado: 'carregando' });
    setVersoes({ estado: 'carregando' });
    setConfirmando(false);
    setArquivo(null);
    setErroArquivo(null);
    setRodada((r) => r + 1);
    void carregarVersoes(sel);
    void getJson<{ rascunho: Rascunho }>(
      `/api/competencias/${encodeURIComponent(sel)}/balancete/rascunho`,
    ).then((d) => {
      if (selAtual.current !== sel) return;
      setRascunho(d ? { estado: 'ok', rascunho: d.rascunho } : { estado: 'erro' });
    });
  }, [sel, carregarVersoes]);

  if (comps === null) return <Spinner />;
  if (comps === 'erro') {
    return <Alert kind="danger">Não foi possível carregar o balancete.</Alert>;
  }
  if (comps.length === 0) return <EmptyState>Nenhuma competência aberta.</EmptyState>;

  const competencia = comps.find((c) => String(c.id) === sel);
  const dados = versoes.estado === 'ok' ? versoes.dados : null;
  const publicada = dados?.balancetes.find((b) => b.status === 'publicado') ?? null;
  // A lista vem da versão mais nova para a mais antiga: a primeira não publicada é a corrente.
  const corrente = dados?.balancetes.find((b) => b.status !== 'publicado') ?? null;
  const divergencias = dados?.fechamento.divergencias ?? [];
  const bloqueada = divergencias.length > 0;
  const podePublicar = editavel && !publicada && corrente?.pdfDocumentoId != null;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (ocupado) return;
    if (!arquivo) {
      setErroArquivo(MSG_SEM_ARQUIVO);
      return;
    }
    if (!/\.pdf$/i.test(arquivo.name)) {
      setErroArquivo(MSG_NAO_PDF);
      return;
    }
    setErroArquivo(null);
    const form = new FormData();
    form.set('pdf', arquivo);
    form.set('status', 'revisao');
    form.set('versao', String(corrente?.versao ?? 1));
    if (rascunho.estado === 'ok') {
      form.set('saldoInicial', String(rascunho.rascunho.saldoInicial));
      form.set('saldoFinal', String(rascunho.rascunho.saldoFinal));
    }

    const alvo = sel;
    setOcupado(true);
    const res = await mutateJson<{ error?: string }>(
      `/api/competencias/${encodeURIComponent(alvo)}/balancete`,
      form,
    );
    setOcupado(false);
    if (selAtual.current !== alvo) return;
    if (!res.ok) {
      const codigo = res.data?.error;
      setAviso({ kind: 'danger', texto: (codigo && ERRO_GERAL[codigo]) || ERRO_ENVIO_PADRAO });
      if (codigo === 'balancete_publicado') await carregarVersoes(alvo);
      return;
    }
    setArquivo(null);
    setRodada((r) => r + 1);
    setAviso({ kind: 'success', texto: 'Balancete enviado para revisão.' });
    await carregarVersoes(alvo);
  }

  async function publicar() {
    if (ocupado || !corrente) return;
    const alvo = sel;
    setOcupado(true);
    const res = await mutateJson<{ error?: string }>(`/api/balancetes/${corrente.id}/publicar`, {});
    setOcupado(false);
    if (selAtual.current !== alvo) return;
    setConfirmando(false);
    if (res.ok) {
      setAviso({ kind: 'success', texto: 'Balancete publicado.' });
    } else if (res.data?.error === 'fechamento_divergente') {
      // O motivo aparece no bloqueio permanente, que a recarga traz.
      setAviso(null);
    } else {
      const codigo = res.data?.error;
      setAviso({ kind: 'danger', texto: (codigo && ERRO_GERAL[codigo]) || ERRO_PUBLICAR_PADRAO });
    }
    await carregarVersoes(alvo);
  }

  return (
    <div className={styles.stack}>
      <Field label="Competência">
        {(id) => (
          <Select
            id={id}
            value={sel}
            onChange={(e) => {
              setAviso(null);
              setSel(e.target.value);
            }}
            style={{ maxWidth: '12rem' }}
          >
            {comps.map((c) => (
              <option key={c.id} value={c.id}>
                {mesAno(c)}
              </option>
            ))}
          </Select>
        )}
      </Field>

      {aviso ? <Alert kind={aviso.kind}>{aviso.texto}</Alert> : null}

      <Card title="Rascunho do balancete">
        {rascunho.estado === 'carregando' ? <Spinner /> : null}
        {rascunho.estado === 'erro' ? (
          <Alert kind="danger">Não foi possível carregar o rascunho do balancete.</Alert>
        ) : null}
        {rascunho.estado === 'ok' ? <Secoes rascunho={rascunho.rascunho} /> : null}
      </Card>

      <Card title="Balancete enviado">
        {versoes.estado === 'carregando' ? <Spinner /> : null}
        {versoes.estado === 'erro' ? (
          <Alert kind="danger">Não foi possível carregar o balancete enviado.</Alert>
        ) : null}
        {dados && dados.balancetes.length === 0 ? (
          <EmptyState>Nenhum balancete enviado para esta competência.</EmptyState>
        ) : null}
        {dados && dados.balancetes.length > 0 ? (
          <ul className={styles.list}>
            {dados.balancetes.map((b) => (
              <li key={b.id} className={styles.listItem} style={{ cursor: 'default' }}>
                <span>v{b.versao}</span>
                <Badge tone={b.status === 'publicado' ? 'success' : undefined}>
                  {STATUS[b.status]}
                </Badge>
                {b.resultado !== null ? <span>Resultado {brl(b.resultado)}</span> : null}
                {b.pdfDocumentoId !== null ? (
                  <a
                    href={`/api/documentos/${encodeURIComponent(b.pdfDocumentoId)}/download`}
                    aria-label={`Baixar PDF da versão ${b.versao}`}
                  >
                    Baixar PDF
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}

        {publicada ? (
          <p className="muted">
            Publicado em {publicada.publicadoEm ? dataUtcBr(publicada.publicadoEm) : '—'}. A edição
            está travada.
          </p>
        ) : null}

        {dados && editavel && !publicada && bloqueada ? (
          <Alert kind="warning">
            {MSG_DIVERGENCIA}
            <ul>
              {divergencias.map((d) => (
                <li key={`${d.saldoCalculado}-${d.saldoDeclarado}`}>
                  {`Calculado ${brl(d.saldoCalculado)} · declarado ${brl(d.saldoDeclarado)} · diferença ${brl(d.diferenca)}`}
                </li>
              ))}
            </ul>
            <Button size="sm" variant="secondary" onClick={() => navigate('/conferencia')}>
              Abrir conferência
            </Button>
          </Alert>
        ) : null}

        {dados && editavel && !publicada ? (
          <form onSubmit={enviar} noValidate>
            <div className={styles.row}>
              <Field label="PDF do balancete" error={erroArquivo}>
                {(id) => (
                  <Input
                    key={rodada}
                    id={id}
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
                  />
                )}
              </Field>
              <Button type="submit" disabled={ocupado}>
                Enviar PDF
              </Button>
            </div>
          </form>
        ) : null}

        {dados && editavel && !publicada && !podePublicar ? (
          <p className="muted">Envie o PDF do balancete para poder publicar.</p>
        ) : null}

        {podePublicar && competencia ? (
          confirmando ? (
            <div className={styles.row}>
              <span>
                {`Publicar o balancete de ${mesAno(competencia)}? Proprietários e moradores passam a vê-lo e a edição fica travada.`}
              </span>
              <Button disabled={ocupado} onClick={() => void publicar()}>
                Confirmar publicação
              </Button>
              <Button variant="ghost" onClick={() => setConfirmando(false)}>
                Cancelar publicação
              </Button>
            </div>
          ) : (
            <Button disabled={bloqueada} onClick={() => setConfirmando(true)}>
              Publicar balancete
            </Button>
          )
        ) : null}
      </Card>
    </div>
  );
}
